/**
 * Shared visual primitives for the OEM Home bottom-strip cards (Phase 2).
 *
 * Ports of the small helpers in the reference
 * `design/gripp-os/apps/web/src/pages/home/HomeOEM.tsx` (ProgressBar) to RNW +
 * Crop Circle tokens. Kept here because Routines and Campaigns both use the bar.
 */
import React from 'react';
import { StyleSheet, View, type DimensionValue } from 'react-native';
import { tokens } from '@gripp/shared-ui';

export interface ProgressBarProps {
  /** Current value. */
  value: number;
  /** Maximum value (the bar fills to value/max). */
  max: number;
  /** Fill colour. */
  color: string;
  testID?: string;
}

/**
 * Thin rounded progress bar — a slate track with a coloured fill. Mirrors the
 * reference ProgressBar (h-2 rounded track, animated fill); rendered static here
 * (no entrance animation needed for the card rows).
 */
export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max,
  color,
  testID,
}) => {
  const pct = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  return (
    <View style={styles.track} testID={testID}>
      <View
        style={[
          styles.fill,
          { width: `${pct * 100}%` as DimensionValue, backgroundColor: color },
        ]}
      />
    </View>
  );
};

ProgressBar.displayName = 'ProgressBar';

const styles = StyleSheet.create({
  track: {
    height: 8, // design: h-2
    flex: 1,
    borderRadius: 4,
    backgroundColor: tokens.colors.slate[100], // --surface-inverse-soft
    overflow: 'hidden',
  },
  fill: {
    height: '100%',
    borderRadius: 4,
  },
});
